import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

/**
 * DiaryDateNavigator — iter959 (Feb 2026)
 *
 * Compact date-picker widget for the Diary "Meal Log" tab. Lets the
 * user step backward up to 30 days (matching the back-fill window
 * enforced by ``POST /api/meals/add``) or forward up to today. The
 * selected date is lifted to DiaryPage state and passed as
 * ``selectedDate`` prop to <MealLog />, which already knows how to
 * fetch/write to a specific date.
 */

// Local YYYY-MM-DD helper (avoids UTC drift that .toISOString() causes
// for users east of UTC — Sofia is UTC+2/+3).
function _todayLocalYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function _addDays(ymd, delta) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function _formatHuman(ymd, bg) {
  try {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(bg ? "bg-BG" : "en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return ymd;
  }
}

export default function DiaryDateNavigator({
  selectedDate,
  onDateChange,
  bg = false,
  isDark = false,
  maxDaysBack = 30,
}) {
  const today   = useMemo(_todayLocalYmd, []);
  const minDate = useMemo(() => _addDays(today, -maxDaysBack), [today, maxDaysBack]);
  const current = selectedDate || today;

  const canGoPrev = current > minDate;
  const canGoNext = current < today;
  const isToday   = current === today;
  const isYesterday = current === _addDays(today, -1);

  const stepDay = (delta) => {
    const next = _addDays(current, delta);
    if (next < minDate || next > today) return;
    onDateChange?.(next === today ? null : next);
  };

  const goToday = () => onDateChange?.(null);
  const goYesterday = () => onDateChange?.(_addDays(today, -1));

  const humanLabel = isToday
    ? (bg ? "Днес" : "Today")
    : isYesterday
      ? (bg ? "Вчера" : "Yesterday")
      : _formatHuman(current, bg);

  const daysAgo = (() => {
    const [y1, m1, d1] = today.split("-").map(Number);
    const [y2, m2, d2] = current.split("-").map(Number);
    const t1 = new Date(y1, m1 - 1, d1).getTime();
    const t2 = new Date(y2, m2 - 1, d2).getTime();
    return Math.round((t1 - t2) / (86400 * 1000));
  })();

  const containerCls = isDark
    ? "bg-slate-900/60 border-slate-800 text-gray-200"
    : "bg-white border-slate-200 text-gray-800";

  return (
    <div
      className={`flex items-center gap-1.5 rounded-xl border px-2 py-1.5 shadow-sm ${containerCls}`}
      data-testid="diary-date-navigator"
    >
      <button
        type="button"
        onClick={() => stepDay(-1)}
        disabled={!canGoPrev}
        className={`p-1 rounded-md transition ${canGoPrev ? "hover:bg-slate-100 dark:hover:bg-slate-800" : "opacity-30 cursor-not-allowed"}`}
        data-testid="diary-date-prev"
        title={bg ? "Ден назад" : "Previous day"}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-1.5 px-1.5">
        <CalendarDays className="w-3.5 h-3.5 opacity-60" />
        <div className="flex flex-col leading-tight">
          <span className="text-xs font-bold" data-testid="diary-date-label">
            {humanLabel}
          </span>
          {!isToday && (
            <span className="text-[9px] opacity-60 tabular-nums" data-testid="diary-date-days-ago">
              {bg ? `преди ${daysAgo} дни` : `${daysAgo} days ago`}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => stepDay(1)}
        disabled={!canGoNext}
        className={`p-1 rounded-md transition ${canGoNext ? "hover:bg-slate-100 dark:hover:bg-slate-800" : "opacity-30 cursor-not-allowed"}`}
        data-testid="diary-date-next"
        title={bg ? "Ден напред" : "Next day"}
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Native date picker for jumping straight to a specific date */}
      <input
        type="date"
        value={current}
        min={minDate}
        max={today}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          onDateChange?.(v === today ? null : v);
        }}
        className={`text-[10px] px-1 py-0.5 rounded border ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}
        data-testid="diary-date-input"
        aria-label={bg ? "Избор на дата" : "Pick date"}
      />

      {!isToday && (
        <>
          <button
            type="button"
            onClick={goYesterday}
            className={`text-[10px] px-1.5 py-0.5 rounded border ${isDark ? "bg-slate-800 border-slate-700 hover:bg-slate-700" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}
            data-testid="diary-date-yesterday-btn"
          >
            {bg ? "Вчера" : "Yesterday"}
          </button>
          <button
            type="button"
            onClick={goToday}
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
            data-testid="diary-date-today-btn"
          >
            {bg ? "Днес" : "Today"}
          </button>
        </>
      )}
    </div>
  );
}
